import React, { useCallback, useContext, useEffect, useState } from 'react'
import { Breadcrumbs } from '@/components/ui/breadcrumb'
import { SortDescriptor } from '@progress/kendo-data-query'
import {
  GridColumn as Column,
  Grid,
  GridCellProps,
  GridSortChangeEvent,
} from '@progress/kendo-react-grid'
import { RateMarkupDto } from 'api/Exchange-Rates/RateMarkupDto'
import { PermissionsEnum } from 'api/Identity/Types/PermissionsEnum'
import { PageData } from 'api/Pagination/Types'
import AddButton from 'components/AddButton/AddButton'
import { Box } from 'components/Box'
import { Pager } from 'components/components/Pager'
import ConfirmationModal from 'components/ConfirmationModal/ConfirmationModal'
import { FilterType } from 'components/Filter'
import { getAdministrationRateMarkupsFields } from 'components/Filter/AdministrationFields/getAdministrationRateMarkupsFields'
import { SelectFiltersFacade } from 'components/Filter/ClientFields/SelectFiltersFacade'
import { Loading } from 'components/Loading'
import MoreButton from 'components/MoreButton/MoreButton'
import { successToast, ToastContext } from 'components/Toast/Toast'
import { createGridState, useGridFilter, useGridState, useGridStatusState } from 'features/DataGrid'
import { isGoBack } from 'lib/react-router-dom'
import { useAppDispatch, useAppSelector } from 'store'
import { CompositeFilterDescriptor, GridFilterActionState } from 'store/grid'
import { fetchPermissionsAction } from 'store/identity/identity.actions'
import { getWalletCurrenciesAction, getWalletTypesAction } from 'store/wallet/wallet-actions'
import { PageQuery } from 'utils/api'
import { useApiClient } from 'utils/api/ApiClient'
import { ExchangeRatesApiClient } from 'utils/api/exchangeRatesApi'
import { IdentityApiClient } from 'utils/api/identityApi'
import { WalletsApiClient } from 'utils/api/walletsApiClient'
import { AuthSessionContext } from 'utils/AuthContext'
import { links } from 'utils/featureToggles'
import { isEmpty } from 'utils/is-empty'
import { getPaginationInitialState } from 'utils/paginationInitialState'
import { hasPermission } from 'utils/permissionsValidator'
import { buildQuery, buildSortQuery } from 'utils/QueryHelpers'
import stringComparator from 'utils/stringComparator'
import { TableStateContext, TableStateContextData } from 'utils/TableStateContext'

import { AddEditRateMarkupForm, AddEditRateMarkupFormValues } from './AddEditRateMarkupForm'
import { rateMarkupsColumns } from './columns'

const RateMarkupsPage = (): JSX.Element => {
  const crumbs = [{ title: 'Rate Markups', link: links.administrationRateMarkups.url }]
  const [auth] = useContext(AuthSessionContext)
  const [tableData, setTableData] = useState<TableStateContextData>({ skipCount: true })

  const identityApiClient = useApiClient(IdentityApiClient)
  const walletsApiClient = useApiClient(WalletsApiClient)

  const exchangeRatesApiClient = useApiClient(ExchangeRatesApiClient)

  const dispatch = useAppDispatch()

  const setToast = useContext(ToastContext)
  const [rateMarkups, setRateMarkups] = useState<RateMarkupDto[]>([])
  const [isAddEditRate, setAddEditRate] = useState<{
    data: RateMarkupDto | undefined
    visible: boolean
  }>({
    data: undefined,
    visible: false,
  })
  const [isDeleteRate, setDeleteRate] = useState<{
    data: RateMarkupDto | undefined
    visible: boolean
  }>({
    data: undefined,
    visible: false,
  })

  const { gridStatusState, gridStatusActions } = useGridStatusState()
  const { gridState, gridActions } = useGridState()
  const { selectFilterForGoBackHistory } = useGridFilter(gridStatusActions)

  const permissions = auth?.permissions || []

  const walletTypes = useAppSelector((state) => state.wallet.walletType)
  const walletCurrencies = useAppSelector((state) => state.wallet.currencies)

  useEffect(() => {
    ;(async () => {
      await fetchGrid(
        async (gridFilterState: GridFilterActionState): Promise<PageData<RateMarkupDto[]>> => {
          const searchQuery = getQuery(gridFilterState.filter)
          const sortQuery = getSort(gridFilterState.sort)
          const response = await exchangeRatesApiClient.fetchRateMarkups({
            search: searchQuery,
            sort: sortQuery,
            pageIndex: gridFilterState.pageIndex,
            pageSize: gridFilterState.take,
            skipCount: tableData?.skipCount !== false,
          })

          if (isEmpty(permissions)) {
            dispatch(fetchPermissionsAction(identityApiClient))
          }
          if (isEmpty(walletTypes)) {
            dispatch(getWalletTypesAction(walletsApiClient))
          }
          if (isEmpty(walletCurrencies)) {
            dispatch(getWalletCurrenciesAction(walletsApiClient))
          }

          if (response?.items) {
            setRateMarkups(sortRateMarkups(response.items))
          }
          setTableData({ ...tableData, meta: response })
          return response
        }
      )
    })()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const fetchGrid = async (
    callback: (gridFilterState: GridFilterActionState) => Promise<PageData<RateMarkupDto[]>>
  ) => {
    gridStatusActions.setLoading(true)

    const gridFilterState = selectFilterForGoBackHistory(
      createGridState(getPaginationInitialState(), gridState, { isGoBack })
    )

    const response = await callback(gridFilterState)
    if (response?.totalResults >= 0) {
      const total = response.totalResults

      gridActions.setGridFilters({
        total,
      })
    }

    gridStatusActions.setLoading(false)
  }

  const getQuery = useCallback(
    (filter?: CompositeFilterDescriptor) => {
      return buildQuery(filter ?? gridState.filter) ?? null
    },
    [gridState.filter]
  )

  const getSort = useCallback(
    (sortDescriptors?: SortDescriptor[]): string | null => {
      return buildSortQuery(sortDescriptors ?? gridState.sort)
    },
    [gridState.sort]
  )

  const filterFetch = useCallback(
    async (query?: PageQuery): Promise<void> => {
      const searchQuery = query?.search ?? getQuery()
      const sortQuery = query?.sort ?? getSort(gridState.sort)
      const pageIndex = query?.pageIndex ?? gridState.pageIndex ?? 1
      const pageSize = query?.pageSize ?? gridState.pageSize ?? 10
      const take = pageSize
      const skip = (pageIndex - 1) * pageSize

      const response = await exchangeRatesApiClient.fetchRateMarkups({
        search: searchQuery,
        sort: sortQuery,
        pageIndex,
        pageSize,
        skipCount: tableData?.skipCount !== false,
      })
      const total = response.totalResults

      if (response?.items) {
        setRateMarkups(response.items)
      }
      gridActions.setGridFilters({
        take,
        skip,
        total,
        pageIndex,
      })
      setTableData({ ...tableData, meta: response })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      getQuery,
      getSort,
      gridActions,
      gridState.pageIndex,
      gridState.pageSize,
      gridState.skip,
      gridState.sort,
      gridState.take,
      tableData?.skipCount,
    ]
  )

  const handleSkipCountChange = async (skipCount: boolean) => {
    const searchQuery = getQuery()
    const sortQuery = getSort(gridState.sort)

    const response = await exchangeRatesApiClient.fetchRateMarkups({
      search: searchQuery,
      sort: sortQuery,
      pageIndex: gridState.pageIndex,
      pageSize: gridState.take,
      skipCount,
    })
    const total = response.totalResults

    if (response?.items) {
      setRateMarkups(response.items)
    }
    gridActions.setGridFilters({
      total,
    })
    setTableData({ ...tableData, skipCount, meta: response })
  }

  const handleSortChange = async (event: GridSortChangeEvent) => {
    gridActions.setGridFilters({
      sort: event.sort,
    })

    const searchQuery = getQuery(gridState.filter)
    const sortQuery = getSort(event.sort)
    const response = await exchangeRatesApiClient.fetchRateMarkups({
      search: searchQuery,
      sort: sortQuery,
      pageIndex: gridState.pageIndex,
      pageSize: gridState.pageSize,
    })

    if (response?.items) {
      setRateMarkups(response.items)
    }
  }

  const handlePageSizeChange = async (pageSize: number) => {
    await gridActions.setGridFilters({
      ...gridState,
      pageSize,
    })

    const searchQuery = getQuery()
    const sortQuery = getSort(gridState.sort)

    const response = await exchangeRatesApiClient.fetchRateMarkups({
      search: searchQuery,
      sort: sortQuery,
      pageIndex: 1,
      pageSize,
      skipCount: tableData?.skipCount !== false,
    })
    await gridActions.setGridFilters({
      ...gridState,
      pageIndex: 1,
      take: pageSize,
      total: response.totalResults,
    })
    if (response?.items) {
      setRateMarkups(response.items)
    }
    setTableData({ ...tableData, meta: response })
  }

  const handleGridPageChange = async (page: number) => {
    const searchQuery = getQuery()
    const sortQuery = getSort(gridState.sort)
    const pageIndex = page
    gridActions.setGridFilters({
      pageIndex,
    })

    const response = await exchangeRatesApiClient.fetchRateMarkups({
      search: searchQuery,
      sort: sortQuery,
      pageIndex,
      pageSize: gridState.take,
      skipCount: tableData?.skipCount !== false,
    })
    const total = response.totalResults

    gridActions.setGridFilters({
      total,
    })
    if (response?.items) {
      setRateMarkups(response.items)
    }
    setTableData({ ...tableData, meta: response })
  }

  const handleEdit = (rateMarkup: RateMarkupDto) => {
    setAddEditRate({ visible: true, data: rateMarkup })
  }

  const handleDeleteConfirmation = (roleItem: RateMarkupDto) => {
    setDeleteRate({ data: roleItem, visible: true })
  }

  const handleConfirmationDelete = async () => {
    if (isDeleteRate.data) {
      try {
        await exchangeRatesApiClient.deleteRateMarkup(isDeleteRate.data.id)
        setToast(successToast('Rate Markup deleted.'))
        await filterFetch()

        handleAddEditCancel()
        handleConfirmationCancel()
      } catch (error) {
        console.error(error)
        handleAddEditCancel()
        handleConfirmationCancel()
      }
    }
  }

  const handleAddEditCancel = () => {
    setAddEditRate({ data: undefined, visible: false })
  }

  const handleConfirmationCancel = () => {
    setDeleteRate({ visible: false, data: undefined })
  }

  const onSubmit = async (values: AddEditRateMarkupFormValues) => {
    if (values.id) {
      await exchangeRatesApiClient.updateRateMarkup(values.id, {
        id: values.id,
        markup: values.markup,
        precision: values.precision,
      })
    } else {
      const { id, ...createValues } = values
      await exchangeRatesApiClient.addRateMarkup(createValues)
    }
    setToast(successToast(`Rate Markup ${values.id ? 'updated' : 'created'}`))
    setAddEditRate({ visible: false, data: undefined })
    await filterFetch()
  }

  return (
    <React.Fragment>
      <Breadcrumbs crumbs={crumbs} />
      <SelectFiltersFacade
        gridState={gridState}
        gridStatusActions={gridStatusActions}
        gridStatusState={gridStatusState}
        setGridFilters={gridActions.setGridFilters}
        filterFetch={filterFetch}
        filterType={FilterType.RATE_MARKUPS}
        filterFields={[...getAdministrationRateMarkupsFields()]}
        getQuery={getQuery}
      />
      <Box display='flex' flexDirection='row'>
        <AddButton
          text='Add new'
          disabled={!hasPermission(permissions, PermissionsEnum.CanCreateMarkup)}
          onClick={() => setAddEditRate({ visible: true, data: undefined })}
        />
      </Box>
      <Loading isLoading={gridStatusState.isLoading}>
        {rateMarkups && (
          <TableStateContext.Provider value={[tableData, setTableData]}>
            <Grid
              {...gridState}
              resizable
              data={rateMarkups}
              sort={gridState.sort}
              total={gridState.total}
              take={gridState.take}
              skip={gridState.skip}
              onSortChange={handleSortChange}
              sortable={{
                allowUnsort: true,
                mode: 'multiple',
              }}
              pageable={true}
              pager={() => (
                <Pager
                  onSkipCountChange={handleSkipCountChange}
                  onPageChange={handleGridPageChange}
                  onPageSizeChange={handlePageSizeChange}
                  page={gridState.pageIndex}
                  pageSize={gridState.pageSize}
                  take={gridState.take || 10}
                  skip={gridState.skip || 0}
                />
              )}
              style={{ zIndex: 0 }}
            >
              {rateMarkupsColumns
                .filter((c) => c.show)
                .map((column, idx) => (
                  <Column key={idx} {...column} />
                ))}
              <Column
                width={50}
                title=''
                cell={(props: GridCellProps) => (
                  <td>
                    <MoreButton
                      items={[
                        {
                          name: 'Edit',
                          'data-test': 'edit',
                          disabled: !hasPermission(permissions, PermissionsEnum.CanEditMarkup),
                          onClick: () => handleEdit(props.dataItem),
                        },
                        {
                          name: 'Delete',
                          'data-test': 'delete',
                          disabled: !hasPermission(permissions, PermissionsEnum.CanDeleteMarkup),
                          onClick: () => handleDeleteConfirmation(props.dataItem),
                        },
                      ]}
                      data-test='more-button'
                    />
                  </td>
                )}
              />
            </Grid>
          </TableStateContext.Provider>
        )}
      </Loading>
      {isDeleteRate.data && (
        <ConfirmationModal
          yesButtonText='Yes, delete'
          onYes={handleConfirmationDelete}
          onCancel={handleConfirmationCancel}
          visible={isDeleteRate.visible}
          renderTitle={
            <div>
              <p>
                Are you sure you want to
                <br />
                delete the following rate markup record?
              </p>

              <div>
                <div id='field'>
                  <strong>Debit Wallet Type</strong> {isDeleteRate.data.debitWalletType.name}
                </div>
                <div id='field'>
                  <strong>Debit Currency</strong> {isDeleteRate.data.debitCurrency.id}
                </div>
                <div id='field'>
                  <strong>Credit Wallet Type</strong> {isDeleteRate.data.creditWalletType.name}
                </div>
                <div id='field'>
                  <strong>Credit Currency</strong> {isDeleteRate.data.creditCurrency.id}
                </div>
                <div id='field'>
                  <strong>Markup</strong> {isDeleteRate.data.markup}
                </div>
              </div>
            </div>
          }
        />
      )}
      {isAddEditRate.visible && (
        <AddEditRateMarkupForm
          isOpen={isAddEditRate.visible}
          onClose={handleAddEditCancel}
          rateMarkup={isAddEditRate.data}
          walletType={walletTypes}
          walletCurrencies={walletCurrencies}
          permissions={permissions}
          onSubmit={onSubmit}
        />
      )}
    </React.Fragment>
  )
}

const sortRateMarkups = (dtos: RateMarkupDto[]) => {
  if (!dtos) {
    return []
  }
  return dtos.sort((a, b) => stringComparator(a.id, b.id))
}

export default RateMarkupsPage
